import { formatDateDisplay, getTodayDateStringInBusinessTZ } from '../../utils/date';

/**
 * Quick relative ranges, in the style of Grafana/Kibana time pickers.
 *
 * Nearly every question an admin brings to the Finances tables is "what
 * happened recently", so those windows get one click. An explicit start/end
 * pair is still reachable behind "Custom" for the rarer audit-style lookup.
 */
export type DateRangePresetId = '2d' | '7d' | '30d' | '90d' | 'all' | 'custom';

export interface DateRangeValue {
  preset: DateRangePresetId;
  /** YYYY-MM-DD, or '' for an unbounded edge. */
  startDate: string;
  endDate: string;
}

export const RELATIVE_PRESETS: { id: DateRangePresetId; days: number; label: string; name: string }[] = [
  { id: '2d', days: 2, label: '2d', name: 'Last 2 days' },
  { id: '7d', days: 7, label: '7d', name: 'Last 7 days' },
  { id: '30d', days: 30, label: '30d', name: 'Last 30 days' },
  { id: '90d', days: 90, label: '90d', name: 'Last 90 days' },
];

export const DEFAULT_DATE_RANGE_PRESET: DateRangePresetId = '30d';

/** Shifts a YYYY-MM-DD string by whole days without going through device timezone. */
export function shiftDateString(dateString: string, deltaDays: number): string {
  const [year, month, day] = dateString.split('-').map(Number);
  const shifted = new Date(Date.UTC(year, month - 1, day) + deltaDays * 86_400_000);
  const y = shifted.getUTCFullYear();
  const m = String(shifted.getUTCMonth() + 1).padStart(2, '0');
  const d = String(shifted.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Resolves a preset to the concrete bounds the finance RPCs take. Relative
 * ranges stay open-ended at the top so rows created later today still appear.
 */
export function resolveDateRangePreset(preset: DateRangePresetId): { startDate: string; endDate: string } {
  const relative = RELATIVE_PRESETS.find(p => p.id === preset);
  if (!relative) return { startDate: '', endDate: '' };
  return { startDate: shiftDateString(getTodayDateStringInBusinessTZ(), -relative.days), endDate: '' };
}

export function createDateRangeValue(preset: DateRangePresetId = DEFAULT_DATE_RANGE_PRESET): DateRangeValue {
  return { preset, ...resolveDateRangePreset(preset) };
}

/** Human-readable summary of the window actually in force. */
export function describeDateRange(value: DateRangeValue): string {
  if (value.preset === 'all') return 'All time';
  if (!value.startDate && !value.endDate) return 'All time';
  const from = value.startDate ? formatDateDisplay(value.startDate) : 'the beginning';
  const to = value.endDate ? formatDateDisplay(value.endDate) : 'now';
  return `${from} → ${to}`;
}
